import { MessageDto, FullMessageDto, CreateMessageDto, PatchMessageDto, ConversationDto } from "../interfaces/MessageInterfaces";

class MessageApi {
  // Get messages for a user (users get their own, managers can specify User_guid)
  static async getMessages(from: number = 0, to: number = 50, userGuid?: string): Promise<FullMessageDto[]> {
    const params = new URLSearchParams({
      from: from.toString(),
      to: to.toString()
    });

    if (userGuid) {
      params.append('User_guid', userGuid);
    }

    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/messages?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const messages: FullMessageDto[] = await response.json();
      return messages;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch messages' }));
      throw new Error(errorData.message || 'Failed to fetch messages');
    }
  }

  // Send a new message
  static async sendMessage(messageData: CreateMessageDto): Promise<FullMessageDto> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(messageData),
    });

    if (response.ok) {
      const message: FullMessageDto = await response.json();
      return message;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to send message' }));
      throw new Error(errorData.message || 'Failed to send message');
    }
  }

  // Mark message as seen
  static async markMessageAsSeen(messageGuid: string, seen: boolean = true): Promise<FullMessageDto> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/messages/${messageGuid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        Message_UserSeen: seen
      }),
    });

    if (response.ok) {
      const message: FullMessageDto = await response.json();
      return message;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to update message' }));
      throw new Error(errorData.message || 'Failed to update message');
    }
  }

  // Manager-only: Get unseen messages from users
  static async getUnseenMessages(): Promise<FullMessageDto[]> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/messages/manager/unseen`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const messages: FullMessageDto[] = await response.json();
      return messages;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch unseen messages' }));
      throw new Error(errorData.message || 'Failed to fetch unseen messages');
    }
  }

  // Manager-only: Get conversations list with search filters
  static async getConversations(params: {
    from?: number;
    to?: number;
    User_FirstName?: string;
    User_LastName?: string;
    /** Any part of the client's name — matched against first and last name alike. */
    User_Name?: string;
    User_Email?: string;
    Message_ManagerSeen?: boolean;
  } = {}): Promise<ConversationDto[]> {
    const queryParams = new URLSearchParams();
    
    if (params.from !== undefined) queryParams.append('from', params.from.toString());
    if (params.to !== undefined) queryParams.append('to', params.to.toString());
    if (params.User_FirstName) queryParams.append('User_FirstName', params.User_FirstName);
    if (params.User_LastName) queryParams.append('User_LastName', params.User_LastName);
    if (params.User_Name) queryParams.append('User_Name', params.User_Name);
    if (params.User_Email) queryParams.append('User_Email', params.User_Email);
    if (params.Message_ManagerSeen !== undefined) queryParams.append('Message_ManagerSeen', params.Message_ManagerSeen.toString());

    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/messages/manager/conversations?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const conversations: ConversationDto[] = await response.json();
      return conversations;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch conversations' }));
      throw new Error(errorData.message || 'Failed to fetch conversations');
    }
  }

  // Manager-only: Delete a message
  static async deleteMessage(messageGuid: string): Promise<void> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/messages/manager/${messageGuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to delete message' }));
      throw new Error(errorData.message || 'Failed to delete message');
    }
  }
}

export default MessageApi;